import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { MdExpandMore, MdExpandLess, MdPushPin, MdBusiness, MdOutlineHandshake } from 'react-icons/md';
import { useHistory } from 'react-router';
import { useFetchAccountSearch } from '../../../queries/useFetchAccountSearch';
import { useFetchOpportunitySearch } from '../../../queries/useFetchOpportunitySearch';
import { getShortcutDisplay } from '../../../hooks/useKeyboardShortcuts';
import {getDatasetPinId} from '../../../libs/navigationPinUtils';
import { colors, spacing, radius, typography, transitions } from '../../../containers/RecipeDesigner/styles/designTokens';

/**
 * Dataset Discovery Component
 *
 * Unified search and exploration interface for datasets, accounts AND opportunities.
 * Features:
 * - Integrated search with live filtering (datasets + accounts + opportunities)
 * - Account and opportunity search with API integration and debouncing
 * - Exact account ID / opportunity ID match prioritization
 * - Opportunity results navigate to the owning account, deep-linked via ?opp=
 * - Compact, icon-free layout for maximum information density
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Cmd/Ctrl-click to open in new tab
 * - Recent accounts and opportunities tracking
 * - Progressive disclosure via popover flyout
 * - Category grouping
 */

const SectionHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${spacing.md} 0 ${spacing.sm} 0`,
  cursor: 'pointer',
  transition: transitions.default,
  '&:hover': {
    backgroundColor: colors.background.subtle,
  },
}));

const SearchField = styled(InputBase)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: `${spacing.sm} ${spacing.md}`,
  fontSize: '0.875rem',
  fontWeight: 400,
  backgroundColor: colors.background.subtle,
  borderRadius: radius.md,
  border: `1px solid ${colors.border.subtle}`,
  transition: transitions.default,

  '&.Mui-focused': {
    backgroundColor: '#FFFFFF',
    borderColor: colors.accent.primary,
    boxShadow: `0 0 0 1px ${colors.accent.primaryBorderLight}`,
  },

  '& input': {
    minWidth: 0,
    padding: 0,
    '&::placeholder': {
      color: colors.text.tertiary,
      fontWeight: 400,
      opacity: 1,
    },
  },
}));

const DatasetList = styled(Box)(() => ({
  maxHeight: '400px',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 0,
  margin: `0 -${spacing.md}`,

  '& > *:last-child': {
    marginBottom: 0,
    paddingBottom: 0,
  },

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: colors.border.default,
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: colors.border.hover,
  },
}));

const CategoryHeader = styled(Typography)(() => ({
  ...typography.sectionTitle,
  padding: `${spacing.md} ${spacing.md} ${spacing.sm} ${spacing.md}`,
  color: colors.text.tertiary,
}));

const DatasetItem = styled(Box)(({ selected }) => ({
  position: 'relative',
  padding: `${spacing.sm} ${spacing.md} ${spacing.sm} ${spacing.md}`,
  paddingLeft: spacing.md,
  marginBottom: '2px',
  cursor: 'pointer',
  borderRadius: radius.sm,
  transition: transitions.default,
  backgroundColor: selected ? colors.accent.primaryLight : 'transparent',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: spacing.xs,
    bottom: spacing.xs,
    width: 3,
    borderRadius: '0 2px 2px 0',
    backgroundColor: selected ? colors.accent.primary : 'transparent',
    transition: transitions.default,
  },

  '&:hover': {
    backgroundColor: selected
      ? colors.accent.primaryLight
      : colors.background.subtle,
  },

  '&:hover .dataset-pin-button': {
    opacity: 1,
  },

  '&:focus-visible': {
    outline: 'none',
    backgroundColor: colors.accent.primaryLight,
    '&::before': {
      backgroundColor: colors.accent.primary,
    },
  },

  '&:last-child': {
    marginBottom: 0,
  },
}));

const PinButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: spacing.sm,
  right: spacing.sm,
  padding: 4,
  opacity: 0,
  transition: 'opacity 0.2s',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: colors.accent.primaryLight,
    color: colors.accent.primary,
  },
}));

/**
 * Simple fuzzy search for datasets
 */
function normalizeSearchText(text) {
  if (!text) return '';

  return text
    .toString()
    .replace(/([a-z])([A-Z])/g, '$1 $2') // activityLevels -> activity Levels
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function fuzzySearch(text, query) {
  if (!text || !query) return 0;

  const textNormalized = normalizeSearchText(text);
  const queryNormalized = normalizeSearchText(query);

  if (!textNormalized || !queryNormalized) return 0;

  if (textNormalized === queryNormalized) return 100;
  if (textNormalized.startsWith(queryNormalized)) return 90;
  if (textNormalized.includes(queryNormalized)) return 70;

  // Also compare compact strings so "activitylevels" matches "activity levels"
  const compactText = textNormalized.replace(/\s+/g, '');
  const compactQuery = queryNormalized.replace(/\s+/g, '');

  if (compactText === compactQuery) return 85;
  if (compactText.startsWith(compactQuery)) return 75;
  if (compactText.includes(compactQuery)) return 65;

  const textTokens = textNormalized.split(' ').filter(Boolean);
  const queryTokens = queryNormalized.split(' ').filter(Boolean);
  const textTokenSet = new Set(textTokens);
  const queryTokenSet = new Set(queryTokens);

  if (queryTokens.length > 1 && queryTokens.every((token) => textTokenSet.has(token))) {
    return 60;
  }

  if (textTokens.length > 0 && textTokens.every((token) => queryTokenSet.has(token))) {
    return 55;
  }

  if (queryTokens.length > 1) {
    const matchedQueryTokens = queryTokens.filter((token) => textTokenSet.has(token)).length;
    const queryCoverage = matchedQueryTokens / queryTokens.length;
    if (queryCoverage >= 0.5) {
      return Math.round(queryCoverage * 50);
    }
  }

  return 0;
}

function getSearchMatchSummary(dataset = {}, query = '') {
  const searchDataset = dataset || {};
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return '';

  if (searchDataset.matchedTab?.label) {
    return `Matched section: ${searchDataset.matchedTab.label}`;
  }

  const aliasMatch = (searchDataset.searchAliases || []).find((alias) => fuzzySearch(alias, query) > 0);
  if (aliasMatch) {
    return `Matched synonym: ${aliasMatch}`;
  }

  const tagMatch = (searchDataset.tags || []).find((tag) => fuzzySearch(tag, query) > 0);
  if (tagMatch) {
    return `Matched topic: ${tagMatch}`;
  }

  if (fuzzySearch(searchDataset.label, query) > 0) {
    return `Matched dataset name`;
  }

  if (fuzzySearch(searchDataset.description, query) > 0) {
    return `Matched description`;
  }

  return 'Matched this search';
}

function formatSearchMatchSummary(summary = '') {
  return summary
    .replace(/^Matched section:\s*/i, '')
    .replace(/^Matched synonym:\s*/i, 'Synonym: ')
    .replace(/^Matched topic:\s*/i, 'Topic: ')
    .replace(/^Matched\s+/i, '');
}

function accountMatchesSearch(account, query) {
  if (!query?.trim()) return false;

  return fuzzySearch(account?.Name, query) > 0 || fuzzySearch(account?.Id, query) > 0;
}

function opportunityMatchesSearch(opportunity, query) {
  if (!query?.trim()) return false;

  return fuzzySearch(opportunity?.OppName, query) > 0 || fuzzySearch(opportunity?.OppId, query) > 0;
}

/**
 * Opportunities have no route of their own — they resolve to the owning account
 * view, deep-linked so AccountView can surface the specific deal. account_id is
 * nullable in crm_opportunity, so an orphaned opportunity has no destination.
 */
function buildOpportunityPath(opportunity) {
  if (!opportunity?.AccountId) return null;

  return `/accounts/${opportunity.AccountId}?opp=${encodeURIComponent(opportunity.OppId)}`;
}

/**
 * Search and filter datasets
 * Searches across: label, description, category, tags, tool metadata, and tabs
 */
function isCurrentDataset(dataset, currentPath) {
  if (!dataset?.path || !currentPath) return false;

  if (currentPath === dataset.path) return true;

  if (dataset.matchedTab?.path) {
    return currentPath === dataset.matchedTab.path
      || currentPath.startsWith(`${dataset.matchedTab.path}/`);
  }

  if (dataset.matchedTab?.value !== undefined && dataset.matchedTab?.value !== null) {
    const matchedTabPath = `${dataset.path}/${dataset.matchedTab.value}`;
    return currentPath === matchedTabPath || currentPath.startsWith(`${matchedTabPath}/`);
  }

  return currentPath.startsWith(`${dataset.path}/`);
}

function filterDatasets(datasets, query, currentPath = '') {
  if (!query || query.trim() === '') return datasets;

  return datasets
    .map((dataset, originalIndex) => {
      const labelScore = fuzzySearch(dataset.label, query) * 3;
      const descriptionScore = fuzzySearch(dataset.description, query) * 2;
      const categoryScore = fuzzySearch(dataset.category, query);

      // Search through tags
      const tagsScore = Math.max(
        0,
        ...(dataset.tags || []).map(tag => fuzzySearch(tag, query))
      ) * 2;

      // Search through explicit aliases/synonyms
      const aliasScore = Math.max(
        0,
        ...(dataset.searchAliases || []).map(alias => fuzzySearch(alias, query))
      ) * 2;

      // Search through tabs (if present)
      let tabScore = 0;
      let matchedTab = null;
      if (dataset.tabs?.length > 0) {
        dataset.tabs.forEach(tab => {
          const tabIdScore = fuzzySearch(tab.id, query) * 2;
          const tabValueScore = fuzzySearch(tab.value, query) * 2;
          const tabLabelScore = fuzzySearch(tab.label, query) * 2.5;
          const tabKeywordsScore = Math.max(
            0,
            ...(tab.keywords || []).map(keyword => fuzzySearch(keyword, query))
          ) * 2;

          const currentTabScore = tabIdScore + tabValueScore + tabLabelScore + tabKeywordsScore;
          if (currentTabScore > tabScore) {
            tabScore = currentTabScore;
            matchedTab = tab;
          }
        });
      }

      // Search through tool metadata (primary tool)
      let toolScore = 0;
      if (dataset.tool) {
        toolScore += fuzzySearch(dataset.tool.name, query) * 2;
        toolScore += fuzzySearch(dataset.tool.cmetadata?.summary, query);

        // Search analytic_intent, domain, entity_type arrays
        const analyticIntentScore = Math.max(
          0,
          ...(dataset.tool.cmetadata?.analytic_intent || []).map(intent => fuzzySearch(intent, query))
        );
        const domainScore = Math.max(
          0,
          ...(dataset.tool.cmetadata?.domain || []).map(domain => fuzzySearch(domain, query))
        );
        const entityTypeScore = Math.max(
          0,
          ...(dataset.tool.cmetadata?.entity_type || []).map(entity => fuzzySearch(entity, query))
        );

        toolScore += Math.max(analyticIntentScore, domainScore, entityTypeScore) * 1.5;
      }

      // Search through related tools metadata
      let relatedToolsScore = 0;
      if (dataset.relatedToolsData?.length > 0) {
        dataset.relatedToolsData.forEach(tool => {
          relatedToolsScore += fuzzySearch(tool.name, query);
          relatedToolsScore += fuzzySearch(tool.cmetadata?.summary, query) * 0.5;

          const analyticIntentScore = Math.max(
            0,
            ...(tool.cmetadata?.analytic_intent || []).map(intent => fuzzySearch(intent, query))
          );
          const domainScore = Math.max(
            0,
            ...(tool.cmetadata?.domain || []).map(domain => fuzzySearch(domain, query))
          );
          const entityTypeScore = Math.max(
            0,
            ...(tool.cmetadata?.entity_type || []).map(entity => fuzzySearch(entity, query))
          );

          relatedToolsScore += Math.max(analyticIntentScore, domainScore, entityTypeScore);
        });
      }

      const routeAndTabIntentBonus = aliasScore > 0 && tabScore > 0 ? 200 : 0;
      const totalScore = (
        labelScore
        + descriptionScore
        + categoryScore
        + tagsScore
        + aliasScore
        + tabScore
        + routeAndTabIntentBonus
        + toolScore
        + relatedToolsScore
      );

      return {
        dataset,
        score: totalScore,
        matchedTab,
        originalIndex,
        isCurrent: isCurrentDataset({ ...dataset, matchedTab }, currentPath),
      };
    })
    .filter(result => result.score > 0)
    .sort((a, b) => {
      // Opening search implies navigation intent. Keep the current destination
      // discoverable, but never rank it ahead of another matching dataset.
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? 1 : -1;

      return (b.score - a.score) || (a.originalIndex - b.originalIndex);
    })
    .map(result => ({
      ...result.dataset,
      matchedTab: result.matchedTab
    }));
}

/**
 * Group datasets by category
 * Categories are ordered by priority, then by their highest-scoring dataset
 */
function groupByCategory(datasets, prioritizeCategories = true) {
  const groups = {};

  // Define category priority (lower number = higher priority)
  const getCategoryPriority = (category) => {
    const normalized = category?.toUpperCase();
    switch (normalized) {
      case 'PREDICT': return 1;
      case 'STATS': return 2;
      case 'SETUP': return 99; // Always last
      default: return 50; // Default priority for unknown categories
    }
  };

  // Group datasets and track max score per category
  datasets.forEach((dataset, index) => {
    if (!groups[dataset.category]) {
      groups[dataset.category] = {
        datasets: [],
        maxScore: index, // Lower index = higher score (datasets already sorted)
        priority: getCategoryPriority(dataset.category),
      };
    }
    groups[dataset.category].datasets.push(dataset);
  });

  // Sort categories by priority first, then by their highest-scoring dataset
  return Object.entries(groups)
    .sort((a, b) => {
      if (prioritizeCategories && a[1].priority !== b[1].priority) {
        return a[1].priority - b[1].priority;
      }

      return a[1].maxScore - b[1].maxScore;
    })
    .map(([category, data]) => ({
      category,
      datasets: data.datasets,
    }));
}

/**
 * One opportunity row. Shared by the "Exact Match" and "Opportunities" sections
 * so the two stay visually identical apart from the accent treatment.
 */
function OpportunityResultItem({ opportunity, onNavigate, emphasized = false }) {
  const path = buildOpportunityPath(opportunity);
  const isNavigable = Boolean(path);

  const row = (
    <DatasetItem
      selected={false}
      onClick={isNavigable ? (e) => onNavigate(path, e, 'opportunity', opportunity) : undefined}
      tabIndex={isNavigable ? 0 : -1}
      role="button"
      aria-disabled={!isNavigable}
      aria-label={opportunity.OppName}
      sx={isNavigable ? undefined : { opacity: 0.5, cursor: 'default' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <MdOutlineHandshake
          size={18}
          color={emphasized ? colors.accent.primary : colors.text.secondary}
          style={{ flexShrink: 0 }}
        />
        <Typography
          title={opportunity.OppName}
          sx={{
            fontSize: '0.875rem',
            fontWeight: emphasized ? 600 : 500,
            color: emphasized ? colors.accent.primary : colors.text.primary,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {opportunity.OppName}
        </Typography>
      </Box>
    </DatasetItem>
  );

  if (isNavigable) return row;

  return (
    <Tooltip title="This opportunity has no linked account" placement="right">
      <Box>{row}</Box>
    </Tooltip>
  );
}

export default function DatasetDiscovery({
  datasets = [],
  searchDatasets = null,
  onNavigate,
  onPin,
  currentPath = '',
  pinnedDatasetIds = [],
  defaultExpanded = false,
  enableSearchPreview = true,
  onNavigateComplete,
  searchPlaceholder = null,
}) {
  const history = useHistory();
  const shortcutLabel = useMemo(() => getShortcutDisplay('search'), []);
  const resolvedSearchPlaceholder = searchPlaceholder || 'Search QFlow...';

  // Inline collapsible section (collapsed in drawer, expanded in popout when requested)
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [searchPreviewAnchorEl, setSearchPreviewAnchorEl] = useState(null);
  const [searchPreviewDataset, setSearchPreviewDataset] = useState(null);
  const searchInputRef = useRef(null);
  const datasetItemRefs = useRef({});
  const isSearchActive = searchQuery.trim() !== '';

  // Debounce search query for account API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch accounts using debounced search
  const {
    data: accounts = [],
  } = useFetchAccountSearch({ debouncedInputValue: debouncedSearchQuery });

  // Fetch opportunities in parallel — separate endpoint, so a slow Postgres
  // query never holds up the account results.
  const {
    data: opportunities = [],
  } = useFetchOpportunitySearch({ debouncedInputValue: debouncedSearchQuery });

  // Build searchable dataset source (can include pinned routes)
  const searchableDatasets = useMemo(() => {
    const source = Array.isArray(searchDatasets) && searchDatasets.length > 0
      ? searchDatasets
      : datasets;

    const seen = new Set();
    return source.filter((dataset) => {
      if (!dataset?.id || seen.has(dataset.id)) return false;
      seen.add(dataset.id);
      return true;
    });
  }, [searchDatasets, datasets]);

  // Filter datasets based on search query
  const filteredDatasets = useMemo(() => {
    const source = searchQuery.trim() ? searchableDatasets : datasets;
    return filterDatasets(source, searchQuery, currentPath);
  }, [currentPath, datasets, searchableDatasets, searchQuery]);

  // Group filtered datasets by category
  const groupedDatasets = useMemo(() => {
    if (!isSearchActive) return groupByCategory(filteredDatasets);

    const otherDestinations = filteredDatasets.filter(
      (dataset) => !isCurrentDataset(dataset, currentPath)
    );
    const currentDestinations = filteredDatasets.filter(
      (dataset) => isCurrentDataset(dataset, currentPath)
    );

    return [
      ...groupByCategory(otherDestinations, false),
      ...(currentDestinations.length > 0
        ? [{ category: 'Current view', datasets: currentDestinations }]
        : []),
    ];
  }, [currentPath, filteredDatasets, isSearchActive]);

  // Flat list in the same order the datasets are rendered on screen.
  // Keyboard navigation must walk this order, not the score-sorted
  // `filteredDatasets` order — otherwise Arrow keys skip items whenever
  // grouping rearranges the visual sequence.
  const orderedDatasets = useMemo(() => {
    return groupedDatasets.flatMap((group) => group.datasets);
  }, [groupedDatasets]);

  const activeDatasetIndex = useMemo(() => {
    return orderedDatasets.findIndex((dataset) => isCurrentDataset(dataset, currentPath));
  }, [orderedDatasets, currentPath]);

  // Merge accounts with recent accounts for unified options
  const accountOptions = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const uniqueOptions = new Map();
    recentAccounts
      .filter((acc) => accountMatchesSearch(acc, searchQuery))
      .forEach((acc) => uniqueOptions.set(acc.Id, acc));
    accounts.forEach((acc) => {
      if (!uniqueOptions.has(acc.Id) && accountMatchesSearch(acc, searchQuery)) {
        uniqueOptions.set(acc.Id, acc);
      }
    });
    return Array.from(uniqueOptions.values());
  }, [accounts, recentAccounts, searchQuery]);

  // Check for exact account ID match
  const exactAccountMatch = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return accountOptions.find(
      (acc) => acc.Id.toLowerCase() === searchQuery.toLowerCase()
    );
  }, [accountOptions, searchQuery]);

  // Merge opportunities with recent opportunities for unified options
  const opportunityOptions = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const uniqueOptions = new Map();
    recentOpportunities
      .filter((opp) => opportunityMatchesSearch(opp, searchQuery))
      .forEach((opp) => uniqueOptions.set(opp.OppId, opp));
    opportunities.forEach((opp) => {
      if (!uniqueOptions.has(opp.OppId) && opportunityMatchesSearch(opp, searchQuery)) {
        uniqueOptions.set(opp.OppId, opp);
      }
    });
    return Array.from(uniqueOptions.values());
  }, [opportunities, recentOpportunities, searchQuery]);

  // Check for exact opportunity ID match
  const exactOpportunityMatch = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return opportunityOptions.find(
      (opp) => String(opp.OppId).toLowerCase() === searchQuery.toLowerCase()
    );
  }, [opportunityOptions, searchQuery]);

  // Build unified results (accounts + opportunities + datasets)
  const hasResults = filteredDatasets.length > 0
    || accountOptions.length > 0
    || opportunityOptions.length > 0;

  // Reset selected index when results change
  useEffect(() => {
    if (!searchQuery.trim() || filteredDatasets.length === 0) {
      setSelectedIndex(-1);
      return;
    }

    setSelectedIndex(0);
  }, [searchQuery, filteredDatasets.length]);

  const closeSearchPreview = useCallback(() => {
    setSearchPreviewAnchorEl(null);
    setSearchPreviewDataset(null);
  }, []);

  const handleSearchPreviewOpen = useCallback((event, dataset) => {
    if (!enableSearchPreview || !isSearchActive) return;

    setSearchPreviewAnchorEl(event.currentTarget);
    setSearchPreviewDataset(dataset);
  }, [enableSearchPreview, isSearchActive]);

  useEffect(() => {
    if (!enableSearchPreview || !isSearchActive) {
      closeSearchPreview();
    }
  }, [closeSearchPreview, enableSearchPreview, isSearchActive]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Auto-focus search input when EXPLORE is expanded
  useEffect(() => {
    if (!isExpanded) return undefined;

    const focusTimer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(focusTimer);
  }, [isExpanded]);

  const handleNavigate = useCallback((path, event, itemType, itemData, matchedTab) => {
    const normalizedSearchQuery = normalizeSearchText(searchQuery);
    const hasBalanceSheetIntent = normalizedSearchQuery.includes('balance sheet')
      || normalizedSearchQuery.includes('pipeline balance')
      || normalizedSearchQuery.includes('pipeline waterfall');
    const inferredBalanceSheetTab = itemType === 'dataset'
      && itemData?.id === 'pipeline-forensics'
      && hasBalanceSheetIntent
      ? itemData?.tabs?.find((tab) => tab?.value === 'balance-sheet') || { value: 'balance-sheet' }
      : null;
    const selectedTab = inferredBalanceSheetTab || matchedTab;

    // Build full path for matched tab-like destinations.
    let fullPath = path;
    if (selectedTab?.path) {
      fullPath = selectedTab.path;
    } else if (selectedTab && selectedTab.value) {
      // Append tab to path (e.g., /board-stats/arr or /pipeline-forensics/calculator)
      fullPath = `${path}/${selectedTab.value}`;
    }

    // Check for Command (Mac) or Ctrl (Windows/Linux) key
    const isNewTab = event?.metaKey || event?.ctrlKey;

    if (isNewTab) {
      // Open in new window
      window.open(fullPath, '_blank', 'noopener,noreferrer');
      setSearchQuery('');
      onNavigateComplete?.();
      return;
    }

    // Handle account selection (track recent)
    if (itemType === 'account' && itemData) {
      history.push(fullPath);
      setRecentAccounts((prev) => {
        const updated = [itemData, ...prev.filter((acc) => acc.Id !== itemData.Id)];
        return updated.slice(0, 5); // Keep only last 5
      });
      setSearchQuery(''); // Clear search after navigation
      onNavigateComplete?.();
      return;
    }

    // Handle opportunity selection (track recent). The opportunity rides along in
    // location state so AccountView can render its callout even when the deal is
    // missing from the account payload (closed-lost, or truncated long history).
    if (itemType === 'opportunity' && itemData) {
      history.push(fullPath, { selectedOpportunity: itemData });
      setRecentOpportunities((prev) => {
        const updated = [itemData, ...prev.filter((opp) => opp.OppId !== itemData.OppId)];
        return updated.slice(0, 5); // Keep only last 5
      });
      setSearchQuery(''); // Clear search after navigation
      onNavigateComplete?.();
      return;
    }

    // Default: navigate dataset in same window
    if (onNavigate) {
      onNavigate(fullPath, itemData, event);
    }
    setSearchQuery(''); // Clear search after navigation
    onNavigateComplete?.();
  }, [onNavigate, onNavigateComplete, history, searchQuery]);

  const handlePin = useCallback((datasetId, e) => {
    console.log('[DatasetDiscovery] handlePin called with:', datasetId);
    e.stopPropagation();
    if (onPin) {
      console.log('[DatasetDiscovery] Calling onPin');
      onPin(datasetId);
    } else {
      console.warn('[DatasetDiscovery] onPin is not defined!');
    }
  }, [onPin]);

  const handleKeyDown = (e) => {
    if (!isExpanded || orderedDatasets.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === -1) {
            return activeDatasetIndex >= 0
              ? Math.min(activeDatasetIndex + 1, orderedDatasets.length - 1)
              : 0;
          }
          return prev < orderedDatasets.length - 1 ? prev + 1 : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => {
          if (prev === -1) {
            return activeDatasetIndex >= 0
              ? Math.max(activeDatasetIndex - 1, 0)
              : orderedDatasets.length - 1;
          }
          return prev > 0 ? prev - 1 : -1;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (orderedDatasets[selectedIndex]) {
          const selectedDataset = orderedDatasets[selectedIndex];
          handleNavigate(
            selectedDataset.path,
            e,
            'dataset',
            selectedDataset,
            selectedDataset.matchedTab
          );
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsExpanded(false);
        break;
      default:
        break;
    }
  };

  const totalDatasets = datasets.length;
  const searchPreviewOpen = Boolean(isSearchActive && searchPreviewAnchorEl && searchPreviewDataset);
  const searchPreviewMatchSummary = formatSearchMatchSummary(
    getSearchMatchSummary(searchPreviewDataset, searchQuery)
  );

  return (
    <Box sx={{ px: 2, pb: 0 }}>
      <SectionHeader onClick={handleToggle}>
        <Typography
          sx={{
            ...typography.sectionTitle,
          }}
        >
          EXPLORE ({totalDatasets} DATA SETS)
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
        </IconButton>
      </SectionHeader>

      <Collapse
        in={isExpanded}
        sx={{
          p: 0,
          m: 0,
          '& .MuiCollapse-wrapper': { p: 0, m: 0 },
          '& .MuiCollapse-wrapperInner': { p: 0, m: 0 },
        }}
      >
        {/* Search Field */}
        <Box sx={{ pb: 1, mx: '-2px' }}>
          <SearchField
            inputRef={searchInputRef}
            placeholder={resolvedSearchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            endAdornment={
              <Box
                aria-hidden
                sx={{
                  ml: 1,
                  px: 0.75,
                  py: 0.125,
                  flexShrink: 0,
                  borderRadius: '4px',
                  border: `1px solid ${colors.border.subtle}`,
                  color: colors.text.tertiary,
                  backgroundColor: '#FFFFFF',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  letterSpacing: 0,
                }}
              >
                {shortcutLabel}
              </Box>
            }
          />
        </Box>

        {/* Dataset List with Loading Indicator */}
        <DatasetList>
          {/* Exact Account / Opportunity ID Match */}
          {(exactAccountMatch || exactOpportunityMatch) && (
            <Box sx={{ pb: 0, mb: 0 }}>
              <CategoryHeader>Exact Match</CategoryHeader>
              {exactOpportunityMatch && (
                <OpportunityResultItem
                  opportunity={exactOpportunityMatch}
                  onNavigate={handleNavigate}
                  emphasized
                />
              )}
              {exactAccountMatch && (
              <DatasetItem
                selected={false}
                onClick={(e) => handleNavigate(`/accounts/${exactAccountMatch.Id}`, e, 'account', exactAccountMatch)}
                tabIndex={0}
                role="button"
                aria-label={exactAccountMatch.Name}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <MdBusiness size={18} color={colors.accent.primary} style={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: colors.accent.primary,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {exactAccountMatch.Name}
                      </Typography>
                      {/* <Chip
                        label="Account"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: colors.accent.primaryLight,
                          color: colors.accent.primary,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      /> */}
                    </Box>
                  </Box>
                </Box>
              </DatasetItem>
              )}
            </Box>
          )}

          {/* Datasets by Category */}
          {groupedDatasets.map(({ category, datasets: categoryDatasets }) => (
            <Box key={category} sx={{ pb: 0, mb: 0 }}>
              <CategoryHeader>{category}</CategoryHeader>
              {categoryDatasets.map((dataset) => {
                const globalIndex = orderedDatasets.indexOf(dataset);
                const isCurrent = isCurrentDataset(dataset, currentPath);
                const isKeyboardSelected = globalIndex === selectedIndex;
                const isVisuallySelected = isKeyboardSelected || (!isSearchActive && isCurrent);
                const pinId = getDatasetPinId(dataset);
                const isPinned = pinnedDatasetIds.includes(pinId);

                return (
                  <DatasetItem
                    key={dataset.id}
                    ref={(node) => {
                      if (node) {
                        datasetItemRefs.current[dataset.id] = node;
                      } else {
                        delete datasetItemRefs.current[dataset.id];
                      }
                    }}
                    selected={isVisuallySelected}
                    onClick={(e) => handleNavigate(dataset.path, e, 'dataset', dataset, dataset.matchedTab)}
                    onMouseEnter={(e) => handleSearchPreviewOpen(e, dataset)}
                    onMouseLeave={closeSearchPreview}
                    tabIndex={0}
                    role="button"
                    aria-label={dataset.label}
                    aria-current={isCurrent ? 'page' : undefined}
                  >
                    {onPin && !dataset.isPermanent && !isPinned && (
                      <Tooltip
                        title={dataset.matchedTab ? `Pin ${dataset.matchedTab.label}` : 'Pin dataset'}
                        placement="right"
                      >
                        <PinButton
                          className="dataset-pin-button"
                          size="small"
                          onClick={(e) => handlePin(pinId, e)}
                        >
                          <MdPushPin size={14} />
                        </PinButton>
                      </Tooltip>
                    )}
                    <Typography
                      component="div"
                      sx={{ mb: 0.25, pr: 4 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography
                          title={dataset.label}
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: isCurrent ? colors.accent.primary : colors.text.primary,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {dataset.label}
                        </Typography>
                        {dataset.matchedTab && (
                          <Chip
                            label={dataset.matchedTab.label}
                            title={dataset.matchedTab.label}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              flexShrink: 0,
                              maxWidth: '55%',
                              backgroundColor: colors.accent.primaryLight,
                              color: colors.accent.primary,
                              '& .MuiChip-label': {
                                px: 0.75,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: colors.text.tertiary,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        pr: 4,
                      }}
                    >
                      {dataset.description}
                    </Typography>
                  </DatasetItem>
                );
              })}
            </Box>
          ))}


          {/* Account Results */}
          {(() => {
            const filteredAccounts = accountOptions.filter(
              (acc) => !exactAccountMatch || acc.Id !== exactAccountMatch.Id
            );

            return filteredAccounts.length > 0 && (
              <Box sx={{ pb: 0, mb: 0 }}>
                <CategoryHeader>Accounts</CategoryHeader>
                {filteredAccounts.map((account) => (
                  <DatasetItem
                    key={account.Id}
                    selected={false}
                    onClick={(e) => handleNavigate(`/accounts/${account.Id}`, e, 'account', account)}
                    tabIndex={0}
                    role="button"
                    aria-label={account.Name}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <MdBusiness size={18} color={colors.text.secondary} style={{ flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.primary,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {account.Name}
                      </Typography>
                    </Box>
                  </DatasetItem>
                ))}
              </Box>
            );
          })()}

          {/* Opportunity Results */}
          {(() => {
            const filteredOpportunities = opportunityOptions.filter(
              (opp) => !exactOpportunityMatch || opp.OppId !== exactOpportunityMatch.OppId
            );

            return filteredOpportunities.length > 0 && (
              <Box sx={{ pb: 0, mb: 0 }}>
                <CategoryHeader>Opportunities</CategoryHeader>
                {filteredOpportunities.map((opportunity) => (
                  <OpportunityResultItem
                    key={opportunity.OppId}
                    opportunity={opportunity}
                    onNavigate={handleNavigate}
                  />
                ))}
              </Box>
            );
          })()}

          {/* No Results */}
          {!hasResults && searchQuery.trim() !== '' && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: colors.text.tertiary,
                }}
              >
                No datasets, accounts, or opportunities found for &quot;{searchQuery}&quot;
              </Typography>
            </Box>
          )}
        </DatasetList>

        <Popover
          open={enableSearchPreview && searchPreviewOpen}
          anchorEl={searchPreviewAnchorEl}
          onClose={closeSearchPreview}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          hideBackdrop
          sx={{ pointerEvents: 'none' }}
          PaperProps={{
            sx: {
              width: 330,
              maxWidth: 'min(360px, calc(100vw - 40px))',
              ml: 1,
              p: 1.5,
              borderRadius: radius.sm,
              border: `1px solid ${colors.border.subtle}`,
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
              backgroundColor: '#FFFFFF',
              pointerEvents: 'none',
            },
          }}
        >
          {searchPreviewDataset && (
            <Box>
              <Typography
                sx={{
                  ...typography.sectionTitle,
                  color: colors.text.tertiary,
                  mb: 0.75,
                }}
              >
                {searchPreviewDataset.category}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: colors.text.primary,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {searchPreviewDataset.label}
                </Typography>
                {searchPreviewDataset.matchedTab?.label && (
                  <Chip
                    label={searchPreviewDataset.matchedTab.label}
                    size="small"
                    sx={{
                      height: 20,
                      maxWidth: 145,
                      flexShrink: 0,
                      fontSize: '0.7rem',
                      backgroundColor: colors.accent.primaryLight,
                      color: colors.accent.primary,
                      '& .MuiChip-label': {
                        px: 0.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                )}
              </Box>

              {searchPreviewDataset.description && (
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: '0.78rem',
                    lineHeight: 1.45,
                    color: colors.text.secondary,
                  }}
                >
                  {searchPreviewDataset.description}
                </Typography>
              )}

              <Box
                sx={{
                  mt: 1.25,
                  pt: 1,
                  borderTop: `1px solid ${colors.border.subtle}`,
                  display: 'grid',
                  gap: 0.5,
                }}
              >
                {searchPreviewMatchSummary && (
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      lineHeight: 1.35,
                      color: colors.text.secondary,
                    }}
                  >
                    <Box component="span" sx={{ color: colors.text.tertiary, fontWeight: 600 }}>
                      Matched:
                    </Box>{' '}
                    {searchPreviewMatchSummary}
                  </Typography>
                )}

              </Box>
            </Box>
          )}
        </Popover>
      </Collapse>
    </Box>
  );
}
